import API_BASE, { withApiCredentials } from "../api";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";

export class AdminAnalyticsApiError extends Error {
  constructor(message, { status = 0, detail = "" } = {}) {
    super(message);
    this.name = "AdminAnalyticsApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function parseJsonResponse(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function getAdminToken() {
  return getActiveAwareAuthToken(
    typeof window !== "undefined" ? window : undefined
  );
}

function normalizeDays(days, fallback = 30) {
  return Math.min(90, Math.max(1, Number.parseInt(days, 10) || fallback));
}

async function getAdminAnalyticsJson(path, { signal } = {}) {
  const token = getAdminToken();

  const response = await fetch(
    `${API_BASE}${path}`,
    withApiCredentials({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal,
    })
  );
  const payload = await parseJsonResponse(response);

  if (!response.ok) {
    const detail =
      typeof payload?.detail === "string" ? payload.detail.trim() : "";
    throw new AdminAnalyticsApiError(
      detail || "Admin analytics request failed",
      {
        status: response.status,
        detail,
      }
    );
  }

  return payload || {};
}

export async function getAdminAnalyticsAccess(options = {}) {
  const payload = await getAdminAnalyticsJson("/admin/analytics/access", options);
  return {
    canAccess: payload?.canAccess === true,
  };
}

export async function getAdminAnalyticsOverview(days = 7, options = {}) {
  const normalizedDays = normalizeDays(days, 7);
  return getAdminAnalyticsJson(
    `/admin/analytics/overview?days=${encodeURIComponent(normalizedDays)}`,
    options
  );
}

function getAdminAnalyticsSection(path, days = 30, options = {}) {
  const normalizedDays = normalizeDays(days, 30);
  return getAdminAnalyticsJson(
    `/admin/analytics/${path}?days=${encodeURIComponent(normalizedDays)}`,
    options
  );
}

export function getAdminAnalyticsUsers(days = 30, options = {}) {
  return getAdminAnalyticsSection("users", days, options);
}

export function getAdminAnalyticsEngagement(days = 30, options = {}) {
  return getAdminAnalyticsSection("engagement", days, options);
}

export function getAdminAnalyticsContent(days = 30, options = {}) {
  return getAdminAnalyticsSection("content", days, options);
}

export function getAdminAnalyticsSocial(days = 30, options = {}) {
  return getAdminAnalyticsSection("social", days, options);
}

export function getAdminAnalyticsMapSpots(days = 30, options = {}) {
  return getAdminAnalyticsSection("map-spots", days, options);
}

export function getAdminAnalyticsGear(days = 30, options = {}) {
  return getAdminAnalyticsSection("gear", days, options);
}

export function getAdminAnalyticsSurveysFeedback(days = 30, options = {}) {
  return getAdminAnalyticsSection("surveys-feedback", days, options);
}

export function getAdminAnalyticsApiCache(days = 30, options = {}) {
  return getAdminAnalyticsSection("api-cache", days, options);
}

export function getAdminAnalyticsSystem(days = 30, options = {}) {
  return getAdminAnalyticsSection("system", days, options);
}
